import type {
  CreateDeal,
  DealItemInput,
  MarginRequest,
  ReasonRequest,
  SpecialTermsDecision,
  SpecialTermsInput,
} from '@psp-aggregator/sdk';
import sdk from '@psp-aggregator/sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface DealPayloadVariables<TPayload> {
  dealId: string;
  payload: TPayload;
}

interface DealItemVariables<TPayload> extends DealPayloadVariables<TPayload> {
  itemId: string;
}

interface SpecialTermsVariables<
  TPayload,
> extends DealPayloadVariables<TPayload> {
  requestId: string;
}

type RevokeVariables = DealPayloadVariables<ReasonRequest> | ReasonRequest;

const useDealInvalidation = () => {
  const queryClient = useQueryClient();

  return (dealId?: string): void => {
    void queryClient.invalidateQueries({ queryKey: ['listDeals'] });
    if (dealId) {
      void queryClient.invalidateQueries({ queryKey: ['getDeal', dealId] });
      void queryClient.invalidateQueries({
        queryKey: ['getDealEvents', dealId],
      });
    }
  };
};

export const useCreateDealMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: (payload: CreateDeal) => sdk.requests.createDeal(payload),
    onSuccess: () => invalidate(),
  });
};

export const useCreateDealLinkMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: async (payload: CreateDeal) => {
      const deal = await sdk.requests.createDeal(payload);

      return sdk.requests.generateDealLink(deal.id);
    },
    onSuccess: () => invalidate(),
  });
};

export const useUpdateDraftDealMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({ dealId, payload }: DealPayloadVariables<CreateDeal>) =>
      sdk.requests.updateDraftDeal(payload, dealId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useAddDealItemMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({ dealId, payload }: DealPayloadVariables<DealItemInput>) =>
      sdk.requests.addDealItem(payload, dealId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useUpdateDealItemMarginMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({
      dealId,
      itemId,
      payload,
    }: DealItemVariables<MarginRequest>) =>
      sdk.requests.updateDealItemMargin(payload, dealId, itemId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useDeleteDealItemMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({
      dealId,
      itemId,
    }: Omit<DealItemVariables<never>, 'payload'>) =>
      sdk.requests.deleteDealItem(dealId, itemId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useGenerateDealLinkMutation = (defaultDealId?: string) => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: (dealId?: string) => {
      const resolvedDealId = dealId ?? defaultDealId;

      if (!resolvedDealId) throw new Error('Deal ID is required');

      return sdk.requests.generateDealLink(resolvedDealId);
    },
    onSuccess: (_data, dealId) => invalidate(dealId ?? defaultDealId),
  });
};

export const useRevokeDealMutation = (defaultDealId?: string) => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: (variables: RevokeVariables) => {
      const nested = 'dealId' in variables;
      const dealId = nested ? variables.dealId : defaultDealId;
      const payload = nested ? variables.payload : variables;

      if (!dealId) throw new Error('Deal ID is required');

      return sdk.requests.revokeDeal(payload, dealId);
    },
    onSuccess: (_data, variables) =>
      invalidate('dealId' in variables ? variables.dealId : defaultDealId),
  });
};

const useReasonMutation = (
  request: (payload: ReasonRequest, dealId: string) => Promise<unknown>,
) => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({ dealId, payload }: DealPayloadVariables<ReasonRequest>) =>
      request(payload, dealId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useTerminateDealMutation = () =>
  useReasonMutation(sdk.requests.terminateDeal);
export const useArchiveDealMutation = () =>
  useReasonMutation(sdk.requests.archiveDeal);

export const useRequestSpecialTermsMutation = () => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({
      dealId,
      payload,
    }: DealPayloadVariables<SpecialTermsInput>) =>
      sdk.requests.requestSpecialTerms(payload, dealId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

const useSpecialTermsDecisionMutation = (
  request: (
    payload: SpecialTermsDecision,
    dealId: string,
    requestId: string,
  ) => Promise<unknown>,
) => {
  const invalidate = useDealInvalidation();

  return useMutation({
    mutationFn: ({
      dealId,
      payload,
      requestId,
    }: SpecialTermsVariables<SpecialTermsDecision>) =>
      request(payload, dealId, requestId),
    onSuccess: (_data, { dealId }) => invalidate(dealId),
  });
};

export const useResolveSpecialTermsMutation = () =>
  useSpecialTermsDecisionMutation(sdk.requests.resolveSpecialTerms);
export const useRejectSpecialTermsMutation = () =>
  useSpecialTermsDecisionMutation(sdk.requests.rejectSpecialTerms);
export const useLeaveProposalContactsMutation = (token: string) =>
  sdk.mutations.useLeaveProposalContacts(token);
export const useAcceptPublicProposalMutation = (token: string) =>
  sdk.mutations.useAcceptPublicProposal(token);
export const useDeclinePublicProposalMutation = (token: string) =>
  sdk.mutations.useDeclinePublicProposal(token);
